import {
  Box,
  Chip,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import { MdLocalShipping, MdOutlinePerson, MdPrint, MdStickyNote2 } from 'react-icons/md';
import { Link } from 'react-router-dom';
import { IOrdersRegisteredOnOmieEntity, OrderStatusEnum } from '../../Services/api/orders/orders.interface';
import { ConfirmDialog } from '../Dialog/variants/dialog-confirmation/dialog-confirmation.component';
import IKanbanBoardOrdersSchemaProps from './interfaces/kanban-board-orders-schema.interface';
import { StrictModeDroppable } from './components/droppable-component';
import { OrderDetailsModal } from '../Modals/variants/order-details/order-details-modal.component';
import { IOrderData } from '../Modals/variants/order-details/order-details-modal.component';
import { ISaleEntity } from '../../Pages/Financial/Details/financial-details.hook';
import { GetOrderAuditTimelineUseCase } from '../../Usecase/Audit/get-order-audit-timeline-usecase';

export interface KanbanTaskContent {
  order: string;
  customer: string;
  carrier: string;
  observations?: string;
  nsOrderId?: string | number;
  customerName?: string;
  carrierName?: string;
  os?: string;
  type?: 'checkbox' | 'click' | 'navigate';
}

export interface Task {
  id: string;
  content: KanbanTaskContent;
  isLocked?: boolean;
}

const COLUMN_THEME: Record<string, { accent: string; label: string }> = {
  [OrderStatusEnum.AWAITING_PICKING]: { accent: '#ed6c02', label: 'Aguardando' },
  [OrderStatusEnum.IN_PICKING]: { accent: '#1976d2', label: 'Em andamento' },
  [OrderStatusEnum.AWAITING_INVOICING]: { accent: '#2e7d32', label: 'Próximo passo' },
};

const ORDER_DETAILS_FROM = 'orders-list';

interface KanbanOrderCardProps {
  task: Task;
  orderDetailsUrl: string;
  canDrag: boolean;
  onOpenDetails: (orderId: string) => void;
}

const KanbanOrderCard: FC<KanbanOrderCardProps> = ({
  task,
  orderDetailsUrl,
  canDrag,
  onOpenDetails,
}) => {
  const customer = task.content.customerName || task.content.customer.replace(/^Cliente:\s*/i, '');
  const carrier = task.content.carrierName || task.content.carrier.replace(/^Transportadora:\s*/i, '');
  const observations = task.content.observations?.replace(/^Observações:\s*/i, '');

  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1.5,
        p: 1.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        opacity: task.isLocked ? 0.55 : 1,
        cursor: task.isLocked ? 'not-allowed' : 'grab',
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
        '&:hover': task.isLocked
          ? {}
          : {
              borderColor: 'primary.main',
              boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
            },
      }}
      onClick={(event) => {
        event.stopPropagation();
        if (!task.isLocked) {
          onOpenDetails(task.id);
        }
      }}
    >
      <Box
        component={Link}
        to={orderDetailsUrl}
        onClick={(event) => event.stopPropagation()}
        sx={{
          color: 'primary.dark',
          fontWeight: 700,
          fontSize: '0.875rem',
          textDecoration: 'none',
          display: 'inline-block',
          mb: 1,
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        {task.content.order}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
          <MdOutlinePerson size={15} style={{ marginTop: 2, flexShrink: 0, opacity: 0.55 }} />
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.35, wordBreak: 'break-word' }}>
            {customer || '—'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
          <MdLocalShipping size={15} style={{ marginTop: 2, flexShrink: 0, opacity: 0.55 }} />
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.35, wordBreak: 'break-word' }}>
            {carrier || '—'}
          </Typography>
        </Box>
        {observations && (
          <Tooltip title={observations} placement="top-start">
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
              <MdStickyNote2 size={15} style={{ marginTop: 2, flexShrink: 0, opacity: 0.55 }} />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  fontStyle: 'italic',
                  lineHeight: 1.35,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {observations}
              </Typography>
            </Box>
          </Tooltip>
        )}
      </Box>

      {!canDrag && (
        <Chip label="Somente leitura" size="small" sx={{ mt: 1, height: 22, fontSize: '0.65rem' }} />
      )}
    </Paper>
  );
};

interface IKanbanBoardOrdersProps {
  initialData?: IKanbanBoardOrdersSchemaProps;
  handleSelectProductToBill?: (productId?: string) => void;
  handleUpdateOrderStatus: (payload: { orderId: string; newStatus: OrderStatusEnum }) => Promise<void>;
  omieDetailsOrder?: IOrdersRegisteredOnOmieEntity;
  handleSelectAllProductsToBill?: () => void;
  billProductsOrder?: (data?: any) => Promise<any>;
  canClick?: boolean;
  canDrag?: boolean;
  productsToBill?: string[];
  setOmieOrderData: (state: any) => void;
  onPrintPickingList?: () => void;
  onCardClick?: (orderId: string) => void;
  orderDetailsData?: {
    sale?: ISaleEntity;
    orderData?: IOrderData;
  };
  onSelectSeller?: () => void;
  onOrderWithoutSeller?: () => void;
  getOrderAuditTimelineUseCase: GetOrderAuditTimelineUseCase;
}

const KanbanBoardOrders: FC<IKanbanBoardOrdersProps> = ({
  initialData,
  handleUpdateOrderStatus,
  canDrag = true,
  onPrintPickingList,
  onCardClick,
  orderDetailsData,
  onSelectSeller,
  onOrderWithoutSeller,
  getOrderAuditTimelineUseCase,
}) => {
  const theme = useTheme();
  const [data, setData] = useState(initialData);
  const [tempMovement, setTempMovement] = useState<{ orderId: string; newStatus: OrderStatusEnum } | undefined>();
  const [showConfirmation, setShowConfirmation] = useState({
    show: false,
    title: '',
    description: '',
  });
  const [orderDetailsModalOpen, setOrderDetailsModalOpen] = useState(false);

  useEffect(() => {
    if (initialData) {
      setData(initialData);
    }
  }, [initialData]);

  const totalTasks = useMemo(
    () => Object.keys(data?.tasks || {}).length,
    [data?.tasks],
  );

  const onCancelDragAction = () => {
    setShowConfirmation({ show: false, title: '', description: '' });
    setTempMovement(undefined);
  };

  const handleCardClick = (orderId: string) => {
    setOrderDetailsModalOpen(true);
    onCardClick?.(orderId);
  };

  const handleCloseOrderDetailsModal = () => {
    setOrderDetailsModalOpen(false);
  };

  const updateLocalKanbanState = (orderId: string, newStatus: OrderStatusEnum) => {
    if (!data) return;

    setData((prevData) => {
      if (!prevData) return prevData;

      const newData = { ...prevData, columns: { ...prevData.columns } };

      Object.keys(newData.columns).forEach((columnId) => {
        const column = newData.columns[columnId as OrderStatusEnum];
        if (column) {
          newData.columns[columnId as OrderStatusEnum] = {
            ...column,
            taskIds: column.taskIds.filter((taskId) => taskId !== orderId),
          };
        }
      });

      const targetColumn = newData.columns[newStatus];
      if (targetColumn) {
        newData.columns[newStatus] = {
          ...targetColumn,
          taskIds: [...targetColumn.taskIds, orderId],
        };
      }

      return newData;
    });
  };

  const handleSubmit = async () => {
    try {
      if (!tempMovement) return;

      await handleUpdateOrderStatus(tempMovement);
      updateLocalKanbanState(tempMovement.orderId, tempMovement.newStatus);
    } catch (error) {
      console.error('Erro ao atualizar status do pedido:', error);
    } finally {
      onCancelDragAction();
    }
  };

  const onDragEnd = (result: any) => {
    const { destination, draggableId } = result;

    if (!destination || !data) return;

    const task = data.tasks[draggableId];
    const endColumn = data.columns[destination.droppableId as OrderStatusEnum];

    if (task?.isLocked) {
      return;
    }

    if (destination.droppableId === result.source.droppableId) {
      return;
    }

    setTempMovement({
      orderId: draggableId,
      newStatus: endColumn?.id as OrderStatusEnum,
    });
    setShowConfirmation({
      show: true,
      title: 'Confirmar movimentação',
      description: `Mover o pedido para "${endColumn?.title}"?`,
    });
  };

  if (!data || !data.columnOrder || data.columnOrder.length === 0) {
    return (
      <Box
        sx={{
          py: 6,
          px: 3,
          textAlign: 'center',
          borderRadius: 2,
          border: '1px dashed',
          borderColor: 'divider',
          bgcolor: alpha(theme.palette.primary.main, 0.03),
        }}
      >
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Nenhum pedido na separação B2B
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Ajuste os filtros ou a busca para encontrar pedidos aguardando separação.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <ConfirmDialog
        open={showConfirmation.show}
        title={showConfirmation.title}
        description={showConfirmation.description}
        onCancel={onCancelDragAction}
        onConfirm={handleSubmit}
      />

      <DragDropContext onDragEnd={onDragEnd}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            pb: 1,
            alignItems: 'flex-start',
            scrollbarWidth: 'thin',
          }}
        >
          {data.columnOrder.map((columnId) => {
            const column = data.columns[columnId];
            const tasks = column?.taskIds?.map((taskId) => data.tasks[taskId]).filter(Boolean) || [];
            const columnTheme = COLUMN_THEME[columnId] || { accent: theme.palette.primary.main, label: '' };

            if (!column) return null;

            return (
              <Box
                key={column.id}
                sx={{
                  flex: '1 1 0',
                  minWidth: { xs: 280, md: 300 },
                  maxWidth: { md: '33.333%' },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    mb: 1.5,
                    px: 0.5,
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight={800} noWrap>
                      {column.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {columnTheme.label}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    <Chip
                      size="small"
                      label={tasks.length}
                      sx={{
                        height: 24,
                        fontWeight: 700,
                        bgcolor: alpha(columnTheme.accent, 0.12),
                        color: columnTheme.accent,
                      }}
                    />
                    {column.id === OrderStatusEnum.IN_PICKING && onPrintPickingList && (
                      <Tooltip title="Imprimir lista de separação">
                        <IconButton size="small" onClick={onPrintPickingList} sx={{ color: 'text.secondary' }}>
                          <MdPrint size={18} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Box>

                <StrictModeDroppable droppableId={column.id}>
                  {(provided, snapshot) => (
                    <Box
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      sx={{
                        p: 1.5,
                        borderRadius: 2.5,
                        minHeight: 160,
                        maxHeight: 'calc(100vh - 340px)',
                        overflowY: 'auto',
                        bgcolor: snapshot.isDraggingOver
                          ? alpha(columnTheme.accent, 0.08)
                          : alpha(theme.palette.grey[100], 0.9),
                        border: '1px solid',
                        borderColor: snapshot.isDraggingOver
                          ? alpha(columnTheme.accent, 0.35)
                          : 'divider',
                        transition: 'background-color 0.2s ease, border-color 0.2s ease',
                        scrollbarWidth: 'thin',
                      }}
                    >
                      {tasks.length === 0 ? (
                        <Box
                          sx={{
                            py: 4,
                            px: 2,
                            textAlign: 'center',
                            borderRadius: 2,
                            border: '1px dashed',
                            borderColor: alpha(columnTheme.accent, 0.25),
                            bgcolor: alpha('#fff', 0.65),
                          }}
                        >
                          <Typography variant="body2" color="text.secondary">
                            Nenhum pedido nesta etapa
                          </Typography>
                        </Box>
                      ) : (
                        tasks.map((task, index) => (
                          <Draggable
                            key={task.id}
                            draggableId={task.id}
                            index={index}
                            isDragDisabled={task.isLocked || !canDrag}
                          >
                            {(dragProvided, dragSnapshot) => (
                              <Box
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                {...(task.isLocked || !canDrag ? {} : dragProvided.dragHandleProps)}
                                sx={{
                                  transform: dragSnapshot.isDragging ? 'rotate(1deg)' : undefined,
                                }}
                              >
                                <KanbanOrderCard
                                  task={task}
                                  orderDetailsUrl={`/financial-details/${task.id}?from=${ORDER_DETAILS_FROM}`}
                                  canDrag={canDrag && !task.isLocked}
                                  onOpenDetails={handleCardClick}
                                />
                              </Box>
                            )}
                          </Draggable>
                        ))
                      )}
                      {provided.placeholder}
                    </Box>
                  )}
                </StrictModeDroppable>
              </Box>
            );
          })}
        </Box>
      </DragDropContext>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          {totalTasks} pedido{totalTasks === 1 ? '' : 's'} no fluxo de separação
        </Typography>
      </Box>

      <OrderDetailsModal
        open={orderDetailsModalOpen}
        onClose={handleCloseOrderDetailsModal}
        sale={orderDetailsData?.sale}
        orderData={orderDetailsData?.orderData}
        onSelectSeller={onSelectSeller}
        onOrderWithoutSeller={onOrderWithoutSeller}
        getOrderAuditTimelineUseCase={getOrderAuditTimelineUseCase}
      />
    </>
  );
};

export { KanbanBoardOrders };
